import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function timeAgo(iso) {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function NotificationBell() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!user || user === false) return;
        const fetch = () => api.get("/me/notifications").then(({ data }) => {
            setItems(data.items || []);
            setUnread(data.unread || 0);
        }).catch(() => {});
        fetch();
        const id = setInterval(fetch, 45000);
        return () => clearInterval(id);
    }, [user]);

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const onOpen = async () => {
        const next = !open;
        setOpen(next);
        if (next && unread > 0) {
            try {
                await api.post("/me/notifications/read");
                setUnread(0);
            } catch {}
        }
    };

    if (!user || user === false) return null;

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={onOpen}
                className="relative p-2 hover:bg-[#1c1c1c] rounded-full transition-colors"
                aria-label="Notifications"
                data-testid="notif-bell"
            >
                <Bell className="w-5 h-5" strokeWidth={1.5} />
                {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 text-[10px] font-semibold bg-[#FF6B9D] text-[#0F0F0F] rounded-full flex items-center justify-center" data-testid="notif-badge">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 mt-2 w-80 md:w-96 bg-[#161616] border border-[#262626] rounded-2xl shadow-2xl overflow-hidden z-50" data-testid="notif-panel">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626]">
                        <p className="font-display text-sm font-semibold">Notifications</p>
                        <button onClick={() => setOpen(false)} className="text-[#6E6E6E] hover:text-[#F4F4F4]"><X className="w-4 h-4" /></button>
                    </div>
                    {items.length === 0 ? (
                        <p className="px-4 py-12 text-center text-sm text-[#6E6E6E]" data-testid="notif-empty">Pas de notifications.</p>
                    ) : (
                        <div className="max-h-96 overflow-y-auto divide-y divide-[#262626]" data-testid="notif-list">
                            {items.slice(0, 15).map((n) => {
                                const content = (
                                    <>
                                        <p className="text-sm font-medium leading-snug">{n.title}</p>
                                        {n.body && <p className="mt-0.5 text-xs text-[#A8A8A8] line-clamp-2">{n.body}</p>}
                                        <p className="mt-1 text-[10px] text-[#6E6E6E] uppercase tracking-widest">{timeAgo(n.created_at)}</p>
                                    </>
                                );
                                return n.link ? (
                                    <Link key={n.id} to={n.link} onClick={() => setOpen(false)} className="block px-4 py-3 hover:bg-[#1c1c1c] transition-colors" data-testid={`notif-item-${n.kind}`}>
                                        {content}
                                    </Link>
                                ) : (
                                    <div key={n.id} className="px-4 py-3" data-testid={`notif-item-${n.kind}`}>
                                        {content}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
